import React, { createContext, useContext, useState, useCallback } from 'react';
import ChatModal from './ChatModal';
import { debugLog } from '../utils/debug';

export const ChatManagerContext = createContext();

export const useChatManager = () => {
  const context = useContext(ChatManagerContext);
  if (!context) {
    throw new Error('useChatManager must be used within a ChatManagerProvider');
  }
  return context;
};

export const ChatManagerProvider = ({ children }) => {
  const [chats, setChats] = useState([]);

  const openChat = useCallback((explanationData) => {
    const chatId = Date.now().toString();
    const newChat = {
      id: chatId,
      explanationData,
      isOpen: true,
      isMinimized: false,
      createdAt: new Date(),
      messages: [],
      conversationId: null,
      isLoading: false
    };
    
    setChats(prev => [...prev, newChat]);
    return chatId;
  }, []);

  const closeChat = useCallback((chatId) => {
    setChats(prev => prev.filter(chat => chat.id !== chatId));
  }, []);

  const minimizeChat = useCallback((chatId) => {
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === chatId ? { ...chat, isMinimized: true } : chat
      );
      return updatedChats;
    });
  }, []);

  const maximizeChat = useCallback((chatId) => {
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === chatId ? { ...chat, isOpen: true, isMinimized: false } : chat
      );
      return updatedChats;
    });
  }, []);

  const getMinimizedChats = useCallback(() => {
    return chats.filter(chat => chat.isMinimized);
  }, [chats]);

  const getOpenChats = useCallback(() => {
    return chats.filter(chat => chat.isOpen && !chat.isMinimized);
  }, [chats]);

  const updateChatState = useCallback((chatId, updates) => {
    setChats(prev => prev.map(chat => 
      chat.id === chatId ? { ...chat, ...(typeof updates === 'function' ? updates(chat) : updates) } : chat
    ));
  }, []);

  const value = {
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    getMinimizedChats,
    getOpenChats,
    updateChatState,
    chats
  };

  return (
    <ChatManagerContext.Provider value={value}>
      {children}
      <ChatManagerRenderer />
    </ChatManagerContext.Provider>
  );
};

const MinimizedChatItem = ({ chat, index, total }) => {
  const { maximizeChat, closeChat } = useChatManager();
  
  const getTruncatedInstruction = (instruction) => {
    if (!instruction) return 'Workout Help';
    
    // Remove markdown formatting and truncate
    const cleanText = instruction.replace(/\*\*(.*?)\*\*/g, '$1').replace(/[#*_`]/g, '');
    return cleanText.length > 40 ? cleanText.substring(0, 40) + '...' : cleanText;
  };

  const getChatTitle = (instruction) => {
    if (!instruction) return 'AI Coach';
    
    // Extract key terms for a more specific title
    const cleanText = instruction.replace(/\*\*(.*?)\*\*/g, '$1').replace(/[#*_`]/g, '');
    
    // Look for common workout terms to create a more specific title
    if (cleanText.toLowerCase().includes('breathing')) return 'Breathing Technique';
    if (cleanText.toLowerCase().includes('pace') || cleanText.toLowerCase().includes('speed')) return 'Pacing Guide';
    if (cleanText.toLowerCase().includes('form') || cleanText.toLowerCase().includes('technique')) return 'Form Tips';
    if (cleanText.toLowerCase().includes('recovery') || cleanText.toLowerCase().includes('rest')) return 'Recovery Advice';
    if (cleanText.toLowerCase().includes('warm') || cleanText.toLowerCase().includes('cool')) return 'Warm-up/Cool-down';
    if (cleanText.toLowerCase().includes('terrain') || cleanText.toLowerCase().includes('surface')) return 'Terrain Tips';
    if (cleanText.toLowerCase().includes('hydration') || cleanText.toLowerCase().includes('water')) return 'Hydration Guide';
    if (cleanText.toLowerCase().includes('negative split')) return 'Pacing Strategy';
    if (cleanText.toLowerCase().includes('cadence') || cleanText.toLowerCase().includes('steps')) return 'Cadence Tips';
    
    // Default to first few words
    const words = cleanText.split(' ').slice(0, 3);
    return words.join(' ') + (cleanText.split(' ').length > 3 ? '...' : '');
  };

  return (
    <div 
      className="bg-gray-800/90 backdrop-blur-sm border border-gray-600 rounded-lg p-3 shadow-lg pointer-events-auto cursor-pointer hover:bg-gray-700/90 transition-all duration-200 transform hover:scale-105"
      onClick={() => {
        maximizeChat(chat.id);
      }}
      style={{
        transform: `translateY(${index * 2}px)`,
        zIndex: 1000 - index
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
            <span className="text-xs font-medium text-blue-300 truncate">
              {getChatTitle(chat.explanationData?.instruction)}
            </span>
          </div>
          <p className="text-xs text-gray-300 mt-1 truncate">
            {getTruncatedInstruction(chat.explanationData?.instruction)}
          </p>
        </div>
        <div className="flex items-center space-x-1 ml-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              debugLog('Maximize button clicked for chat:', chat.id);
              debugLog('Chat state before maximize:', chat);
              maximizeChat(chat.id);
            }}
            className="p-1 hover:bg-gray-600 rounded transition-colors"
            title="Maximize"
          >
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeChat(chat.id);
            }}
            className="p-1 hover:bg-red-600/20 rounded transition-colors"
            title="Close"
          >
            <svg className="w-3 h-3 text-gray-400 hover:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatManagerRenderer = () => {
  const { chats, closeChat } = useChatManager();
  const [minimizedStack, setMinimizedStack] = useState([]);

  // Update minimized stack when chats change
  React.useEffect(() => {
    const minimized = chats.filter(chat => chat.isMinimized);
    setMinimizedStack(minimized);
  }, [chats]);

  return (
    <>
      {/* Render open chats */}
      {chats.map(chat => 
        !chat.isMinimized ? (
          <ChatModal
            key={chat.id}
            chatId={chat.id}
            isOpen={true}
            isMinimized={false}
            onClose={() => closeChat(chat.id)}
            explanationData={chat.explanationData}
          />
        ) : null
      )}
      
      {/* Render minimized chat stack - disabled in favor of workout-modal-specific rendering */}
      {false && minimizedStack.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-50 pointer-events-none">
          <div className="flex flex-col space-y-2 max-w-sm">
            {minimizedStack.map((chat, index) => (
              <MinimizedChatItem
                key={chat.id}
                chat={chat}
                index={index}
                total={minimizedStack.length}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default { ChatManagerProvider, useChatManager };
